import { Component, ChangeDetectionStrategy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RegistrationValidationService } from '../../services/api/registration-validation.service';
import { ValidateRegistrationTokenRequest } from '../../model/signup/request/validate-registration-token-request';
import { CreateEmployeeFromValidationTokenRequest } from '../../model/signup/request/create-employee-from-validation-token-request';
import { CompanyLoginService } from '../../services/api/company-login.service';
import { CompanyLoginInfo } from '../../model/company-login/company-login-info';
import { EmployeeService } from '../../services/api/employee.service';
import { PasswordValidator } from '../../employee/services/password-validator';
import { PasswordService } from '../../services/api/password.service';
import { AuthenticationService } from '../../authentication/authentication.service';

@Component({
  selector: 'app-signup-invitation-landing',
  templateUrl: './signup-invitation-landing.component.html',
  styleUrls: ['./signup-invitation-landing.component.scss'],
  providers: [
    RegistrationValidationService,
    EmployeeService,
    CompanyLoginService,
    PasswordService,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SignupInvitationLandingComponent {
  signUpInformationFormGroup = new FormGroup({
    email: new FormControl('', Validators.email),
    firstName: new FormControl('', Validators.required),
    lastName: new FormControl('', Validators.required),
    password: new FormControl(
      '',
      [Validators.required],
      [PasswordValidator.passwordValid(this.passwordService)],
    ),
  });

  signUpValidationMessages = {
    passwordRequired: {
      errorCode: 'required',
      message: $localize`Passwort wird benötigt`,
    },
    passwordTooShort: {
      errorCode: 'tooShort',
      message: $localize`:@@password_candidateTooShort:Das Passwort ist für die konfigurierte Richtlinie zu kurz.`,
    },
    passwordPolicy: {
      errorCode: 'passwordPolicy',
      message: $localize`:@@password_candidatePolicy:Das Passwort entspricht nicht der konfigurierten Passwortrichtlinie.`,
    },
    passwordValidationUnavailable: {
      errorCode: 'passwordValidationUnavailable',
      message: $localize`:@@password_candidateUnavailable:Die Passwortrichtlinie konnte nicht geprüft werden. Bitte versuchen Sie es erneut.`,
    },
    passwordBlacklisted: {
      errorCode: 'blacklisted',
      message: $localize`Passwort ist zu einfach und nicht erlaubt`,
    },
  };

  isLoading$ = new BehaviorSubject<boolean>(true);
  queryTokenFailed$ = new BehaviorSubject<boolean>(false);
  queryTokenInvalid$ = new BehaviorSubject<boolean>(false);
  phoneNumber$ = new BehaviorSubject<string>('');
  phoneNumberVerified$ = new BehaviorSubject<boolean>(false);
  hidePassword = true;

  private readonly token$ = new BehaviorSubject<string>('');
  companyInfo$ = new BehaviorSubject<CompanyLoginInfo | null>(null);
  protected defaultFavoriteCountryCodeSet = new Set<string>(['AT', 'DE', 'CH']);

  constructor(
    private readonly companyService: CompanyLoginService,
    private readonly registrationValidationService: RegistrationValidationService,
    private readonly employeeService: EmployeeService,
    private readonly passwordService: PasswordService,
    private readonly authenticationService: AuthenticationService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly snackBar: MatSnackBar,
  ) {
    this.signUpInformationFormGroup.controls.email.disable();

    this.companyService
      .getCompanyLoginInformation()
      .subscribe((companyInfo) => {
        this.companyInfo$.next(companyInfo);
      });

    this.activatedRoute.queryParams.subscribe((params) => {
      this.token$.next(params['token']);
      this.queryTokenInformation();
    });
  }

  onPhoneNumberChanged(phoneNumber: string) {
    this.phoneNumber$.next(phoneNumber);
  }

  onPhoneNumberVerifiedChanged(verified: boolean) {
    this.phoneNumberVerified$.next(verified);
  }

  sendAdditionalInformation() {
    this.isLoading$.next(true);
    const requestData = {
      firstName: this.signUpInformationFormGroup.controls.firstName.value,
      lastName: this.signUpInformationFormGroup.controls.lastName.value,
      password: this.signUpInformationFormGroup.controls.password.value,
      phoneNumber: this.phoneNumberVerified$ ? this.phoneNumber$.value : null,
      token: this.token$.value,
    } as CreateEmployeeFromValidationTokenRequest;
    this.employeeService
      .createEmployeeFromValidationToken(requestData)
      .subscribe({
        next: async (x) => {
          this.snackBar.open(
            $localize`Hallo ${x.firstName}! Dein Account wurde erfolgreich angelegt.`,
            undefined,
            { duration: 5000 },
          );
          try {
            await this.authenticationService.login({
              login_hint: x.email ?? undefined,
            });
          } catch {
            await this.router.navigate(['/']);
          }
        },
        error: () => {
          this.isLoading$.next(false);
          this.snackBar.open(
            $localize`Benutzer konnte nicht angelegt werden. Wenden Sie sich an Ihren Administrator`,
            $localize`Ok`,
          );
        },
      });
  }

  retryLoadingToken() {
    this.queryTokenFailed$.next(false);
    this.queryTokenInformation();
  }

  private queryTokenInformation() {
    if (this.token$.value) {
      const request = {
        token: this.token$.value,
      } as ValidateRegistrationTokenRequest;
      this.registrationValidationService.validateToken(request).subscribe({
        next: (validation) => {
          this.signUpInformationFormGroup.controls.email.setValue(
            validation.eMail ?? null,
          );
          if (validation.activatedAt) {
            this.router.navigate(['/']);
          } else if (validation.deletedAt) {
            this.queryTokenInvalid$.next(true);
            this.snackBar.open(
              $localize`Deine Einladung wurde vom Administrator gelöscht.`,
              $localize`Ok`,
              { duration: 3500 },
            );
          } else if (validation.expiredAt) {
            this.queryTokenInvalid$.next(true);
            this.snackBar.open(
              $localize`Deine Einladung ist abgelaufen. Dein Administrator kann dir eine neue ausstellen.`,
              $localize`Ok`,
              { duration: 3500 },
            );
          } else {
            this.isLoading$.next(false);
          }
        },
        error: () => {
          this.queryTokenFailed$.next(true);
        },
      });
    }
  }
}
